import ExternalLinkIcon from "@/assets/icons/info-external-link.svg?react";
import LoadingDotsIcon from "@/assets/icons/loading-dots.svg?react";
import { INFO_EMBARCACAO, LIMITES_CLASSE_VISIVEIS } from "../../../nova-obra.constants";
import { formatDataBr } from "../../../components/format-data";
import styles from "./info-embarcacao.module.scss";
import type { InfoEmbarcacaoProps } from "./types-info-embarcacao";

/** Bloco somente leitura "Informações da Embarcação" (estados: sem seleção, carregando, carregado). */
export function InfoEmbarcacaoView({
  embarcacaoId,
  query,
  onUltimaObra,
  onVerTodosLimites,
}: InfoEmbarcacaoProps) {
  if (!embarcacaoId) {
    return (
      <div
        className={`${styles.card} ${styles.centered} ${styles.vazio}`}
        data-testid="info-embarcacao-vazio"
      >
        <p className={styles.message}>{INFO_EMBARCACAO.empty}</p>
      </div>
    );
  }

  if (query.status === "loading") {
    return (
      <div
        className={`${styles.card} ${styles.centered}`}
        role="status"
        data-testid="info-embarcacao-carregando"
      >
        <p className={styles.message}>{INFO_EMBARCACAO.loading}</p>
        <span className={styles.loading} aria-hidden>
          <LoadingDotsIcon />
        </span>
      </div>
    );
  }

  const info = query.data;
  if (query.status !== "success" || !info) {
    return (
      <div className={`${styles.card} ${styles.centered}`} role="alert">
        <p className={styles.message}>{query.errorMessage ?? INFO_EMBARCACAO.error}</p>
      </div>
    );
  }

  const limites = info.limitesClasse;
  const visiveis = limites.slice(0, LIMITES_CLASSE_VISIVEIS);
  const restantes = limites.length - visiveis.length;

  return (
    <section className={styles.card} data-testid="info-embarcacao">
      <h3 className={styles.title}>{INFO_EMBARCACAO.title}</h3>

      <div className={styles.row}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{INFO_EMBARCACAO.clienteAtual}</span>
          <span className={styles.fieldValue}>{info.clienteAtual || "—"}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{INFO_EMBARCACAO.terminoContratoCampo}</span>
          <span className={styles.fieldValue}>{formatDataBr(info.terminoContrato)}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{INFO_EMBARCACAO.tipoEmbarcacao}</span>
          <span className={styles.fieldValue}>{info.tipoEmbarcacao || "—"}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{INFO_EMBARCACAO.ultimaObra}</span>
          {info.ultimaObra ? (
            <button
              type="button"
              className={styles.link}
              onClick={() => info.ultimaObra && onUltimaObra?.(info.ultimaObra.id)}
            >
              {[info.ultimaObra.codigo && `ID ${info.ultimaObra.codigo}`, info.ultimaObra.nome]
                .filter(Boolean)
                .join(" - ")}
              <ExternalLinkIcon aria-hidden />
            </button>
          ) : (
            <button type="button" className={styles.link} disabled>
              {INFO_EMBARCACAO.semUltimaObra}
            </button>
          )}
        </div>
      </div>

      <div className={styles.summary}>
        <div className={styles.dates}>
          <span>
            <strong>{INFO_EMBARCACAO.terminoContrato}</strong> {formatDataBr(info.terminoContrato)}
          </span>
          <span className={styles.divider} aria-hidden />
          <span>
            <strong>{INFO_EMBARCACAO.previsaoExtensao}</strong>{" "}
            {formatDataBr(info.previsaoExtensaoContratual)}
          </span>
        </div>
        <div className={styles.limites}>
          <strong>{INFO_EMBARCACAO.limitesClasse}</strong>
          {visiveis.map((limite) => (
            <span
              key={`${limite.nome}-${limite.vencimento}`}
              className={`${styles.tag} ${limite.vencido ? styles.tagWarn : styles.tagSecondary}`}
            >
              {limite.nome} · {formatDataBr(limite.vencimento)}
            </span>
          ))}
          {restantes > 0 && (
            <span className={`${styles.tag} ${styles.tagSecondary}`}>+{restantes}</span>
          )}
          {limites.length > 0 && (
            <button type="button" className={styles.verTodos} onClick={onVerTodosLimites}>
              {INFO_EMBARCACAO.verTodos}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
